"use client";

import Link from "next/link";
import type { CBlogContent, CBlogItem } from "@/lib/api/cms-catalog";
import { useT } from "@/lib/i18n/context";
import { cBlogTranslations, type CBlogTranslations } from "./CBlog.i18n";
import { CBlogArticle } from "./CBlogArticle";
import { CatalogLoadMore } from "@/components/cms/Pagination/CatalogLoadMore";
import { ImagePlaceholder } from "@/components/ui/ImagePlaceholder/ImagePlaceholder";
import { extractInternalHref } from "@/lib/utils/extractInternalHref";
import styles from "./CBlog.module.css";

/**
 * CBlog - visitor view komponentu `c_blog` (Blog).
 *
 * **Data source:** `C_BLOG_CONTROLLER::getApiContent()` zwraca:
 *   - `list` - paginowana lista wpisow (item: id, name, short_text,
 *     publication_date, count_view, picture, href)
 *   - `article` - pojedynczy wpis (meta + autor + kategorie) + akapity
 *     (`imp_paragraph`) renderowane przez `<ParagraphRenderer>`.
 *
 * Tresc wpisu = wylacznie akapity (c_blog nie renderuje juz akapitow server-side -
 * `getHtmlParagraphs` usuniety). Media (zdjecia/pliki) siedza w akapitach.
 *
 * **Security:** `short_text` to HTML z admin panelu (trusted) - OK dla
 * `dangerouslySetInnerHTML`.
 */
export function CBlog({ content }: CBlogProps) {
  const t = useT(cBlogTranslations);

  if (!content || typeof content !== "object") {
    return (
      <div className={styles.root}>
        <div className={styles.empty}>{t.emptyData}</div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      {content.view === "article" && content.article ? (
        <CBlogArticle
          article={content.article}
          listHref={content.list_href ?? null}
          t={t}
        />
      ) : (
        <ListView content={content} t={t} />
      )}
    </div>
  );
}

export type CBlogProps = {
  content: CBlogContent;
};

/* List view */

function ListView({ content, t }: { content: CBlogContent; t: CBlogTranslations }) {
  const items: CBlogItem[] = Array.isArray(content.items) ? content.items : [];
  const pagination = content.pagination;

  if (items.length === 0) {
    return <div className={styles.empty}>{t.emptyList}</div>;
  }

  return (
    <CatalogLoadMore<CBlogItem>
      initialItems={items}
      initialPage={pagination?.page ?? 1}
      totalPages={pagination?.total_pages ?? 1}
      totalCount={pagination?.total_count}
      getKey={(item) => item.id}
      extractItems={(c) => {
        const arr = (c as CBlogContent | null)?.items;
        return Array.isArray(arr) ? arr : [];
      }}
    >
      {(allItems) => (
        <ul className={styles.list}>
          {allItems.map((item) => (
            <BlogCard key={item.id} item={item} t={t} />
          ))}
        </ul>
      )}
    </CatalogLoadMore>
  );
}

function BlogCard({ item, t }: { item: CBlogItem; t: CBlogTranslations }) {
  const href = extractInternalHref(item.href);

  const cardInner = (
    <>
      <div className={styles.thumbWrap}>
        {item.picture ? (
          <img
            src={item.picture.thumb_src}
            alt={item.picture.alt || item.name}
            width={item.picture.width ?? undefined}
            height={item.picture.height ?? undefined}
            className={styles.thumb}
            loading="lazy"
          />
        ) : (
          <ImagePlaceholder />
        )}
      </div>
      <div className={styles.body}>
        {item.publication_date && (
          <time className={styles.date} dateTime={item.publication_date}>
            {t.formatDate(item.publication_date)}
          </time>
        )}
        <h2 className={styles.title}>{item.name}</h2>
        <div
          className={styles.short}
          dangerouslySetInnerHTML={{ __html: item.short_text }}
        />
        <span className={styles.more}>{t.readMore}</span>
      </div>
    </>
  );

  return (
    <li className={styles.card}>
      {href !== null ? (
        <Link href={href} className={styles.cardLink} aria-label={item.name}>
          {cardInner}
        </Link>
      ) : (
        <a href={item.href} className={styles.cardLink} aria-label={item.name}>
          {cardInner}
        </a>
      )}
    </li>
  );
}
